/**
 * Thread List Component
 *
 * Shows past conversations with the AI.
 * Uses Convex queries for reactive updates.
 */

import { cn } from "@plasma/utils/cn";
import { formatDate } from "@plasma/utils/time";
import { useAction, useMutation, useQuery } from "convex/react";
import {
  Building2,
  MapPin,
  MessageSquare,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { useState } from "react";

import {
  aiQueriesListThreads,
  aiThreadsDeleteThread,
  aiThreadsUpdateTitle,
} from "~/api-refs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Input } from "~/components/ui/input";
import { ScrollArea } from "~/components/ui/scroll-area";
import { Spinner } from "~/components/ui/spinner";
import { useActiveOrganization } from "~/integrations/vortex-auth/hooks";
import { logger, PortalEvents } from "~/lib/logger";

/** Renders the thread list content based on loading state and data */
function ThreadListContent({
  isLoading,
  threads,
  selectedThreadId,
  onSelectThread,
  setDeleteDialog,
  setRenameDialog,
  onNewThread,
}: {
  isLoading: boolean;
  threads:
    | Array<{
        threadId: string;
        title: string | null;
        isOrgWide: boolean;
        locationId: string | null;
        locationName: string | null;
        createdAt: number;
      }>
    | undefined;
  selectedThreadId?: string | null;
  onSelectThread: (id: string) => void;
  setDeleteDialog: (value: { open: boolean; threadId: string }) => void;
  setRenameDialog: (value: {
    open: boolean;
    threadId: string;
    currentTitle: string;
  }) => void;
  onNewThread: () => void;
}) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Spinner className="h-5 w-5" />
      </div>
    );
  }

  if (threads && threads.length > 0) {
    return (
      <div className="space-y-1 p-2">
        {threads.map((thread) => (
          <ThreadItem
            isSelected={selectedThreadId === thread.threadId}
            key={thread.threadId}
            onDelete={() =>
              setDeleteDialog({ open: true, threadId: thread.threadId })
            }
            onRename={() =>
              setRenameDialog({
                open: true,
                threadId: thread.threadId,
                currentTitle:
                  thread.title ??
                  (thread.isOrgWide
                    ? "All Locations"
                    : (thread.locationName ?? "Conversation")),
              })
            }
            onSelect={() => onSelectThread(thread.threadId)}
            thread={thread}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="text-muted-foreground flex flex-col items-center justify-center p-4 text-center">
      <MessageSquare className="mb-2 h-8 w-8 opacity-50" />
      <p className="text-sm">No conversations yet</p>
      <Button
        className="mt-1"
        onClick={onNewThread}
        size="sm"
        type="button"
        variant="link"
      >
        Start a new one
      </Button>
    </div>
  );
}

interface ThreadListProps {
  /** Currently selected thread ID */
  selectedThreadId: string | null;
  /** Called when a thread is selected */
  onSelectThread: (threadId: string) => void;
  /** Called when user wants to start a new thread */
  onNewThread: () => void;
  /** Optional class name */
  className?: string;
}

export function ThreadList({
  selectedThreadId,
  onSelectThread,
  onNewThread,
  className,
}: ThreadListProps) {
  const { organization } = useActiveOrganization();

  // Fetch threads from Convex
  const threads = useQuery(
    aiQueriesListThreads,
    organization?.id ? { organizationId: organization.id, limit: 50 } : "skip"
  );

  // Mutations for rename and delete
  const updateTitle = useMutation(aiThreadsUpdateTitle);
  const deleteThread = useAction(aiThreadsDeleteThread);

  // Dialog states
  const [renameDialog, setRenameDialog] = useState<{
    open: boolean;
    threadId: string;
    currentTitle: string;
  }>({ open: false, threadId: "", currentTitle: "" });
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    threadId: string;
  }>({ open: false, threadId: "" });
  const [isDeleting, setIsDeleting] = useState(false);

  const handleRename = async (newTitle: string) => {
    if (!newTitle.trim()) {
      return;
    }
    logger.info(PortalEvents.AI_THREAD_RENAMED, {
      thread_id: renameDialog.threadId,
      new_title: newTitle.trim(),
    });
    await updateTitle({
      threadId: renameDialog.threadId,
      title: newTitle.trim(),
    });
    setRenameDialog({ open: false, threadId: "", currentTitle: "" });
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      logger.info(PortalEvents.AI_THREAD_DELETE_CONFIRMED, {
        thread_id: deleteDialog.threadId,
      });
      await deleteThread({ threadId: deleteDialog.threadId });
      // If the deleted thread was selected, start a new one
      if (selectedThreadId === deleteDialog.threadId) {
        onNewThread();
      }
    } finally {
      setIsDeleting(false);
      setDeleteDialog({ open: false, threadId: "" });
    }
  };

  const isLoading = threads === undefined;

  return (
    <div className={cn("flex h-full flex-col", className)}>
      {/* Header */}
      <div className="flex items-center justify-between border-b p-3">
        <span className="text-sm font-medium">Conversations</span>
        <Button
          aria-label="New conversation"
          onClick={onNewThread}
          size="icon"
          type="button"
          variant="ghost"
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      {/* Thread list */}
      <ScrollArea className="flex-1">
        <ThreadListContent
          isLoading={isLoading}
          onNewThread={onNewThread}
          onSelectThread={onSelectThread}
          selectedThreadId={selectedThreadId}
          setDeleteDialog={setDeleteDialog}
          setRenameDialog={setRenameDialog}
          threads={threads}
        />
      </ScrollArea>

      {/* Rename Dialog */}
      <RenameDialog
        currentTitle={renameDialog.currentTitle}
        onOpenChange={(open) =>
          !open &&
          setRenameDialog({ open: false, threadId: "", currentTitle: "" })
        }
        onRename={handleRename}
        open={renameDialog.open}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        onOpenChange={(open) =>
          !open && setDeleteDialog({ open: false, threadId: "" })
        }
        open={deleteDialog.open}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete conversation?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this conversation and all its
              messages. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeleting}
              onClick={handleDelete}
            >
              {isDeleting ? <Spinner className="h-4 w-4" /> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// =============================================================================
// Thread Item Component
// =============================================================================

interface ThreadItemProps {
  thread: {
    threadId: string;
    locationId: string | null;
    locationName: string | null;
    title: string | null;
    isOrgWide: boolean;
    createdAt: number;
  };
  isSelected: boolean;
  onSelect: () => void;
  onRename: () => void;
  onDelete: () => void;
}

function ThreadItem({
  thread,
  isSelected,
  onSelect,
  onRename,
  onDelete,
}: ThreadItemProps) {
  // Format the date
  const formattedDate = formatRelativeDate(thread.createdAt);

  return (
    <div
      className={cn(
        "group flex items-center gap-1 rounded-md transition-colors",
        "hover:bg-accent hover:text-accent-foreground",
        isSelected && "bg-accent text-accent-foreground"
      )}
    >
      <button
        className="min-w-0 flex-1 px-3 py-2 text-left"
        onClick={onSelect}
        type="button"
      >
        <div className="flex items-center gap-2">
          {thread.isOrgWide ? (
            <Building2 className="text-muted-foreground h-3.5 w-3.5 flex-shrink-0" />
          ) : (
            <MapPin className="text-muted-foreground h-3.5 w-3.5 flex-shrink-0" />
          )}
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium">
              {thread.title ??
                (thread.isOrgWide
                  ? "All Locations"
                  : (thread.locationName ?? "Specific Location"))}
            </div>
            <div className="text-muted-foreground text-xs">{formattedDate}</div>
          </div>
        </div>
      </button>

      {/* Actions dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            aria-label="Thread actions"
            className="mr-1 h-7 w-7 opacity-0 group-hover:opacity-100"
            onClick={(e) => e.stopPropagation()}
            size="icon"
            type="button"
            variant="ghost"
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={onRename}>
            <Pencil className="mr-2 h-4 w-4" />
            Rename
          </DropdownMenuItem>
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onClick={onDelete}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

// =============================================================================
// Rename Dialog Component
// =============================================================================

interface RenameDialogProps {
  open: boolean;
  currentTitle: string;
  onOpenChange: (open: boolean) => void;
  onRename: (newTitle: string) => void;
}

function RenameDialog({
  open,
  currentTitle,
  onOpenChange,
  onRename,
}: RenameDialogProps) {
  const [title, setTitle] = useState(currentTitle);

  // Reset title when dialog opens
  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen) {
      setTitle(currentTitle);
    }
    onOpenChange(newOpen);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onRename(title);
  };

  return (
    <Dialog onOpenChange={handleOpenChange} open={open}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rename conversation</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <Input
            autoFocus
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter a name..."
            value={title}
          />
          <DialogFooter className="mt-4">
            <Button
              onClick={() => onOpenChange(false)}
              type="button"
              variant="outline"
            >
              Cancel
            </Button>
            <Button disabled={!title.trim()} type="submit">
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Helpers
// =============================================================================

function formatRelativeDate(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;

  const minutes = Math.floor(diff / 60_000);
  const hours = Math.floor(diff / 3_600_000);
  const days = Math.floor(diff / 86_400_000);

  if (minutes < 1) {
    return "Just now";
  }
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  if (hours < 24) {
    return `${hours}h ago`;
  }
  if (days < 7) {
    return `${days}d ago`;
  }

  return formatDate(new Date(timestamp), "M/D/YYYY");
}
